import { useState } from 'react'
import { Search, Book, Code, FileText, ChevronRight, Home, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

const docCategories = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Home,
    articles: [
      { id: '1', title: 'Introduction to Templatean', time: '5 min' },
      { id: '2', title: 'Quick Start Guide', time: '10 min' },
      { id: '3', title: 'Creating Your First Template', time: '8 min' },
      { id: '4', title: 'Understanding the Dashboard', time: '6 min' },
    ],
  },
  {
    id: 'templates',
    title: 'Templates',
    icon: FileText,
    articles: [
      { id: '5', title: 'Template Basics', time: '7 min' },
      { id: '6', title: 'Advanced Template Features', time: '12 min' },
      { id: '7', title: 'Template Variables and Placeholders', time: '9 min' },
      { id: '8', title: 'Sharing and Collaboration', time: '6 min' },
    ],
  },
  {
    id: 'ai-features',
    title: 'AI Features',
    icon: Book,
    articles: [
      { id: '9', title: 'AI Studio Overview', time: '8 min' },
      { id: '10', title: 'Content Generation Best Practices', time: '10 min' },
      { id: '11', title: 'Custom AI Prompts', time: '7 min' },
      { id: '12', title: 'AI Model Selection', time: '5 min' },
    ],
  },
  {
    id: 'api',
    title: 'API Reference',
    icon: Code,
    articles: [
      { id: '13', title: 'API Introduction', time: '5 min' },
      { id: '14', title: 'Authentication', time: '8 min' },
      { id: '15', title: 'Endpoints Reference', time: '15 min' },
      { id: '16', title: 'Webhooks', time: '7 min' },
    ],
  },
]

const popularArticles = [
  { id: '2', title: 'Quick Start Guide', category: 'Getting Started', views: 2341 },
  { id: '9', title: 'AI Studio Overview', category: 'AI Features', views: 1823 },
  { id: '14', title: 'Authentication', category: 'API Reference', views: 1456 },
  { id: '7', title: 'Template Variables and Placeholders', category: 'Templates', views: 1234 },
]

const sampleDocContent = `
# Quick Start Guide

Welcome to Templatean! This guide will help you get started with our platform in just a few minutes.

## Prerequisites

Before you begin, make sure you have:
- A Templatean account (sign up at [templatean.com](https://templatean.com))
- A modern web browser (Chrome, Firefox, Safari, or Edge)
- Basic understanding of document templates

## Step 1: Create Your First Template

1. Navigate to the Templates section from the sidebar
2. Click the "Create Template" button
3. Choose a template type (Document, Presentation, or Email)
4. Give your template a name and description

## Step 2: Design Your Template

Use our intuitive editor to design your template:

### Adding Content
- Click anywhere in the editor to add text
- Use the toolbar for formatting options
- Insert variables using the {{variable}} syntax

### Using AI Features
1. Click the AI Studio button in the toolbar
2. Describe what content you want to generate
3. Select generation options (tone, length, etc.)
4. Click Generate and watch the magic happen!

## Step 3: Save and Use Your Template

Once you're happy with your template:
1. Click Save in the top-right corner
2. Your template is now available in your library
3. Use it to create new documents instantly

## Next Steps

- Explore [Advanced Template Features](/docs/advanced-templates)
- Learn about [AI Content Generation](/docs/ai-features)
- Set up [API Integration](/docs/api-intro)
`

export default function Documentation() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('getting-started')
  const [selectedArticle, setSelectedArticle] = useState('2')

  const currentCategory = docCategories.find(cat => cat.id === selectedCategory)
  const CategoryIcon = currentCategory?.icon || FileText

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Sidebar */}
      <div className="w-64 border-r border-gray-200 dark:border-gray-700">
        <div className="p-4">
          <Button variant="ghost" size="sm" asChild className="mb-4 w-full justify-start">
            <Link to="/help">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Help
            </Link>
          </Button>

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search docs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>

          <ScrollArea className="h-[calc(100vh-12rem)]">
            <div className="space-y-6">
              {docCategories.map((category) => {
                const Icon = category.icon
                return (
                  <div key={category.id}>
                    <button
                      onClick={() => setSelectedCategory(category.id)}
                      className={cn(
                        'flex items-center gap-2 w-full px-2 py-1.5 text-sm font-medium rounded-md transition-colors',
                        selectedCategory === category.id
                          ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                          : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {category.title}
                    </button>
                    {selectedCategory === category.id && (
                      <div className="mt-2 ml-6 space-y-1">
                        {category.articles.map((article) => (
                          <button
                            key={article.id}
                            onClick={() => setSelectedArticle(article.id)}
                            className={cn(
                              'block w-full text-left px-2 py-1.5 text-sm rounded-md transition-colors',
                              selectedArticle === article.id
                                ? 'bg-primary-50 text-primary-700 dark:bg-primary-950 dark:text-primary-300'
                                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                            )}
                          >
                            {article.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-8">
          <div className="mb-8">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
              <CategoryIcon className="h-4 w-4" />
              <span>{currentCategory?.title}</span>
              <ChevronRight className="h-4 w-4" />
              <span>Quick Start Guide</span>
            </div>
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold">Quick Start Guide</h1>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">10 min read</Badge>
                <Badge variant="secondary">Updated 2 days ago</Badge>
              </div>
            </div>
          </div>

          <Tabs defaultValue="content" className="space-y-4">
            <TabsList>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="related">Related Articles</TabsTrigger>
            </TabsList>

            <TabsContent value="content">
              <Card>
                <CardContent className="prose dark:prose-invert max-w-none p-8">
                  <div dangerouslySetInnerHTML={{ __html: sampleDocContent }} />
                </CardContent>
              </Card>

              <div className="mt-8 flex items-center justify-between">
                <Button variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Previous Article
                </Button>
                <Button variant="outline">
                  Next Article
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="related" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Related Articles</CardTitle>
                  <CardDescription>
                    Other articles that might be helpful
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {currentCategory?.articles
                      .filter(a => a.id !== selectedArticle)
                      .map((article) => (
                        <Link
                          key={article.id}
                          to={`#${article.id}`}
                          className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          <span className="font-medium">{article.title}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {article.time} read
                          </span>
                        </Link>
                      ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Popular Articles</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {popularArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`#${article.id}`}
                        className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <div>
                          <p className="font-medium">{article.title}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {article.category}
                          </p>
                        </div>
                        <Badge variant="secondary">
                          {article.views.toLocaleString()} views
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
